import { Button, Modal, Table, message } from "antd";
import { useAppMutation } from "@/common/hooks/useAppMutation";
import { get, post } from "@/common/services/httpClient";
import type { BankDetail } from "../../types/claimType";
import { useAppQuery } from "@/common/hooks/useAppQuery";
import { useEffect, useState } from "react";
import AddBankDetailForm from "./AddBankDetailModal";
import type { DropdownOption } from "@/common/components/DynamicSelect";
import { useTranslation } from 'react-i18next';
import { hasPermission } from "../../../../common/services/permissionService";
import { getPermissionKeyFromApi } from "../../../../common/components/fromatPermission";
import { API_ENDPOINTS } from "../../api/apiEndpoints";

interface BankDetailsListProps {
    policyHolder: DropdownOption;
    onSuccess: () => void;
    cardNumber?: string;
    modalVisible: boolean;
    onModalClose: () => void; // Added this prop
}

const BankDetailsList = ({ policyHolder, onSuccess, cardNumber, modalVisible }: BankDetailsListProps) => {
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editingBankDetail, setEditingBankDetail] = useState<BankDetail | null>(null);

    const policyHolderId = policyHolder.value as number;

    // Set form visibility to true (add mode) when the modal becomes visible
    useEffect(() => {
        if (modalVisible) {
            setIsFormVisible(true); // Show Add Bank Account form by default
            setEditingBankDetail(null); // Ensure no bank is being edited
        }
    }, [modalVisible]);

    const { data: bankDetails, refetch } = useAppQuery<BankDetail[]>(
        ['bankDetails', policyHolderId],
        async () => {
            const res = await get<{ data: BankDetail[] }>(`/BankDetail/GetByPolicyHolderId/${policyHolderId}`);
            //console.log("Fetched bank details:", res.data);
            return res.data;
        },
        {
            enabled: !!policyHolderId,
        }
    );

    const deleteMutation = useAppMutation(
        (id: number) => post(`/BankDetail/Delete/${id}`, {}),
        {
            onSuccess: () => {
                message.success("Bank detail deleted successfully!");
                refetch();
            },
            onError: (error: Error) => {
                message.error(`Failed to delete bank detail: ${error.message}`);
            },
        }
    );

    const handleAdd = () => {
        setEditingBankDetail(null);
        setIsFormVisible(true);
    };

    const handleEdit = (record: BankDetail) => {
        setEditingBankDetail(record);
        setIsFormVisible(true);
    };

    const { t } = useTranslation();

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: t("Are you sure you want to delete this bank detail?"),
            content: t("This action cannot be undone."),
            okText: t("Yes"),
            okType: "danger",
            cancelText: t("Cancel"),
            onOk: () => {
                deleteMutation.mutate(id);
            },
        });
    };

    const handleFormSuccess = () => {
        setIsFormVisible(false); // Go back to list view after successful save
        setEditingBankDetail(null);
        refetch();
        onSuccess();
    };

    const handleFormCancel = () => {
        setIsFormVisible(false); // Go back to list view on cancel
        setEditingBankDetail(null);
        refetch();
       // onModalClose(); // Call onModalClose to close the parent modal
    };

    const canDelete = hasPermission(getPermissionKeyFromApi(API_ENDPOINTS.CLAIM.DELETE_BANKDETAIL));
    const canEdit = hasPermission(getPermissionKeyFromApi(API_ENDPOINTS.CLAIM.UPDATE_BANK_DETAIL));
    const canAdd = hasPermission(getPermissionKeyFromApi(API_ENDPOINTS.CLAIM.SAVE_BANK_DETAIL));


    const columns = [
        {
            title: t("Bank Name"),
            dataIndex: "bankName",
            key: "bankName",
        },
        {
            title: t("Account Name"),
            dataIndex: "accountName",
            key: "accountName",
        },
        {
            title: t("Account Number"),
            dataIndex: "accountNo",
            key: "accountNo",
        },
        {
            title: t("IBAN"),
            dataIndex: "iban",
            key: "iban",
        },
        {
            title: t("Actions"),
            key: "actions",
            render: (_: unknown, record: BankDetail) => (
                <span>
                    {canEdit &&
                        <Button type="link" onClick={() => handleEdit(record)}>
                            Edit
                        </Button>
                    }
                    {   canDelete && 
                            <Button
                                type="link"
                                danger
                                onClick={() => handleDelete(record.id)}
                                loading={deleteMutation.isPending}
                            >
                                Delete
                            </Button>
                    }
                </span>
            ),
        },
    ];

    if (isFormVisible) {
        return (
            <>
                {canAdd &&
                    <AddBankDetailForm
                        onCancel={handleFormCancel}
                        policyHolderId={policyHolderId}
                        onSuccess={handleFormSuccess}
                        bankDetail={editingBankDetail}
                        cardNumber={cardNumber}
                    />
                }
            </>
        );
    }

    return (
        <div>
            {   canAdd &&
            <Button type="primary" onClick={handleAdd} style={{ marginBottom: 16 }}>
                    {("Add Bank Account")}
            </Button>
            }
            <Table dataSource={bankDetails} columns={columns} rowKey="id" />
        </div>
    );
};

export default BankDetailsList;